import React, { useMemo, useContext, useEffect, useRef, useState } from 'react';
import { getLuminance } from 'polished';
import styled from 'styled-components';

import { $t, ENV, locale, toggleLocale } from 'src/utils';
import { usePartner } from 'src/hooks';
import { Container, Logo, MNavLink, Progress } from 'src/components';
import paths from 'src/constants/paths';

// ICONS
import { ReactComponent as Guides } from 'src/iconic/book.svg';
import { ReactComponent as About } from 'src/iconic/information.svg';
import { ReactComponent as Partners } from 'src/iconic/file-text-2.svg';
import { ReactComponent as Press } from 'src/iconic/announcement.svg';
import { ReactComponent as Blog } from 'src/iconic/edit.svg';
import SessionContext from 'src/context/SessionContext';
import { NavMenu } from 'src/kit/base/NavMenu';
import { useIntercom } from 'src/lib';

export interface NavbarProps {
  banner?: React.ReactNode;
  color?: string;
  darkColor?: string;
  matchColor?: string;
  border?: boolean;
  hideLinks?: boolean;
  hideButtons?: boolean;
  unfix?: boolean;
  transparent?: boolean;
  progress?: number;
  isApp?: boolean;
  bg?: string;
  showBenefitsAdvisor?: boolean;
}

const VisuallyHiddenLink = styled.a`
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
`;

const SNavOuter = styled.div`
  width: 100%;
  max-width: 100%;
  height: calc(var(--s-nav-height));
  top: 0;
  position: ${(p) => (p.unfix ? 'absolute' : 'fixed')};
  z-index: 9999999;
  transition: transform 0.25s;
`;

const SNavBar = styled.div`
  width: 100%;
  max-width: 100%;
  height: var(--s-nav-height);
  bottom: 0;
  z-index: 9999999;
  position: absolute;
  box-sizing: border-box;
  border-bottom: ${(p) => (p.border ? '1px solid var(--c-border)' : '0px solid transparent')};
  transition:
    background 0.25s cubic-bezier(0.42, 0, 0.02, 1.18),
    box-shadow 0.25s cubic-bezier(0.42, 0, 0.02, 1.18);
  padding: 0 24px;
  backdrop-filter: blur(${(p) => (p.transparent ? 0 : 40)}px);
  color: ${(p) =>
    !!p.matchColor && !!p.color ? (getLuminance(p.color) > 0.4 ? 'black' : 'white') : 'var(--fg)'};
  background: ${(p) =>
    p.bg
      ? p.bg
      : !!p.matchColor && !!p.color
        ? p.color
        : p.solid
          ? 'var(--c-bg-nav)'
          : 'transparent'};

  .container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    height: var(--s-nav-height);
  }
`;

const MNav = styled.div`
  background: var(--bg) !important;
  backdrop-filter: blur(40px);
  z-index: 99999999999999999999999999999999999999999;
  position: fixed;
  top: var(--s-nav-height);
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--bgc);
  .navlink,
  .container {
    display: block;
  }
  .container {
    height: 100% !important;
    // padding: var(--side-margin);
  }
  .navlink {
    font-size: var(--fs-h3);
    padding: 12px 0;
  }
  .mnav {
    display: block;
  }
  a {
    font-size: var(--fs-b1) !important;
  }
`;

const links = [
  {
    text: $t({ id: 'nav.explorer', defaultMessage: 'Explore Plans' }),
    path: paths.EXPLORER,
  },
  {
    text: $t({ id: 'nav.resources', defaultMessage: 'Resources' }),
    path: paths.GUIDES_HEALTH,
    children: [
      {
        color: 'var(--g-cerise-light)',
        iconColor: 'var(--c-cerise-0)',
        title: $t({ id: 'nav.blog', defaultMessage: 'Blog' }),
        icon: Blog,
        url: paths.BLOG,
      },
      {
        color: 'var(--g-cerise-light)',
        iconColor: 'var(--c-cerise-0)',
        title: $t({ id: 'nav.guide', defaultMessage: 'Guide' }),
        icon: Guides,
        url: paths.GUIDES_HEALTH,
      },
    ],
  },
  {
    text: $t({ id: 'nav.about', defaultMessage: 'About' }),
    path: paths.ABOUT,
    children: [
      {
        color: 'var(--g-cerise-light)',
        iconColor: 'var(--c-cerise-0)',
        title: $t({ id: 'nav.about', defaultMessage: 'About' }),
        icon: About,
        url: paths.ABOUT,
      },
      {
        color: 'var(--g-cerise-light)',
        iconColor: 'var(--c-cerise-0)',
        title: $t({ id: 'nav.partners', defaultMessage: 'Partners' }),
        icon: Partners,
        url: paths.PARTNERS,
      },
      {
        color: 'var(--g-cerise-light)',
        iconColor: 'var(--c-cerise-0)',
        title: $t({ id: 'nav.press', defaultMessage: 'Press' }),
        icon: Press,
        url: paths.PRESS,
      },
    ],
  },
];

const ToggleNav = () => {
  var el = document.querySelector('body');
  el.classList.toggle('mnav');
};

/**
 *
 */
const Navbar: React.FC<NavbarProps> = ({
  banner,
  color,
  darkColor,
  matchColor,
  border = true,
  hideLinks,
  hideButtons,
  unfix,
  transparent,
  progress = 0,
  isApp,
  bg,
  showBenefitsAdvisor,
}) => {
  const { partner, signupUrl, signinUrl, hideLogo } = useContext(SessionContext);
  const { color: partnerColor, logo } = usePartner({ slug: partner });
  const [navBackground, setNavBackground] = useState(false);

  const navRef = useRef();
  const handleIntercomOpen = useIntercom();

  useEffect(() => {
    const handleScroll = () => {
      const show = window.scrollY > 100;
      if (navRef.current !== show) {
        setNavBackground(show);
      }
    };

    const handleElementScroll = (event) => {
      const element = event.target;
      const show = element.scrollTop > 100;
      if (navRef.current !== show) {
        setNavBackground(show);
      }
    };

    // Attach scroll listener for the window
    document.addEventListener('scroll', handleScroll);

    // Attach scroll listener for the #scroll-content element if it exists
    const scrollContent = document.getElementById('scroll-content');
    if (scrollContent) {
      scrollContent.addEventListener('scroll', handleElementScroll);
    }

    // Cleanup listeners on unmount
    return () => {
      document.removeEventListener('scroll', handleScroll);
      if (scrollContent) {
        scrollContent.removeEventListener('scroll', handleElementScroll);
      }
    };
  }, []);

  const { healthLinks, defaultLinks, mobileLinks, baLink, languageLink } = useMemo(() => {
    const baLink = {
      text: $t({
        id: 'nav.talk-to-advisor',
        defaultMessage: 'Get support',
      }),
      icon: 'Phone',
      path: paths.CONTACT,
      show: showBenefitsAdvisor,
    };

    const helpLink = {
      text: $t({ id: 'nav.help', defaultMessage: 'Help' }),
      icon: 'HelpCircle',
      onClick: handleIntercomOpen,
      show: true,
    };

    const helpLinkNoText = {
      ...helpLink,
      text: undefined,
    };

    const languageLink = {
      text: locale === 'en' ? 'English' : 'Español',
      icon: 'Globe',
      onClick: toggleLocale,
      show: true,
    };

    const signinLink = {
      text: $t({ id: 'nav.sign-in', defaultMessage: 'Sign in' }),
      path: signinUrl || ENV.appSignInUrl,
      show: true,
    };

    const signupLink = {
      text: $t({ id: 'nav.sign-up', defaultMessage: 'Sign up' }),
      path: signupUrl,
      show: true,
    };

    const menuLink = { text: '', icon: 'Menu', onClick: ToggleNav, show: true };

    const healthLinks = [baLink, helpLink, languageLink].filter((link) => link.show);

    const defaultLinks = [helpLinkNoText, signinLink, signupLink].filter((link) => link.show);

    const mobileLinks = [helpLinkNoText, menuLink].filter((link) => link.show);

    return {
      healthLinks,
      defaultLinks,
      mobileLinks,
      baLink,
      languageLink,
    };
  }, [toggleLocale, signinUrl, signupUrl]);

  return (
    <>
      {/* Skip to content link visible only for screen readers */}
      <VisuallyHiddenLink href="#main-content">
        {$t({ id: 'a11y.skip-to-content', defaultMessage: 'Skip to content' })}
      </VisuallyHiddenLink>

      <Progress progress={progress} color={partnerColor} />
      <SNavOuter unfix={unfix}>
        {!!banner && <div className="banner">{banner}</div>}
        <SNavBar
          banner={banner}
          bg={bg}
          solid={navBackground}
          color={color}
          darkColor={darkColor}
          matchColor={matchColor}
          border={border}
          transparent={transparent}
        >
          <Container full>
            <div>
              {!hideLogo && (
                <Logo
                  partner={partner}
                  logo={
                    /**
                     * if partner is not catch, use partner logo,
                     * default: catch logo with rockets
                     */
                    !!partner && partner !== 'catch' && !!logo ? logo : '/lockup-dark.png'
                  }
                  isApp={isApp}
                />
              )}
            </div>

            <div className="no-mobile">{!hideLinks && <NavMenu links={links} />}</div>

            <div>
              <div className="no-mobile">
                {!hideButtons ? <NavMenu links={defaultLinks} /> : <NavMenu links={healthLinks} />}
              </div>
              <div className="mobile-only">
                <NavMenu links={mobileLinks} />
              </div>
            </div>
          </Container>
        </SNavBar>
      </SNavOuter>
      <MNav className="mnav-menu" banner={!!banner} unfix={unfix}>
        <Container>
          <div className="mnav">
            <nav>
              <MNavLink icon={baLink.icon} path={baLink.path} text={baLink.text} />
              {links.map((link) => (
                <MNavLink
                  key={`${link.text}-mobile`}
                  text={link.text}
                  path={!link.children && link.path}
                  onClick={!link.children ? ToggleNav : undefined}
                >
                  {link.children &&
                    link.children.map((resource) => (
                      <MNavLink
                        key={resource.title}
                        text={resource.title}
                        path={resource.url}
                        onClick={ToggleNav}
                      />
                    ))}
                </MNavLink>
              ))}
              <MNavLink
                text={$t({ id: 'nav.sign-in', defaultMessage: 'Sign in' })}
                path={signinUrl}
              />
              <MNavLink
                text={$t({ id: 'nav.sign-up', defaultMessage: 'Sign up' })}
                path={signupUrl}
              />
              <MNavLink
                icon={languageLink.icon}
                text={languageLink.text}
                onClick={languageLink.onClick}
              />
            </nav>
          </div>
        </Container>
      </MNav>
    </>
  );
};

export default Navbar;
